import { Box, Button, Stack, Typography } from "@mui/material";
import { memo, useCallback, useEffect, useRef, useState } from "react";

const OTP_LENGTH = 6;
const OTP_DIGIT_KEYS = ["otp-1", "otp-2", "otp-3", "otp-4", "otp-5", "otp-6"];

type PhoneOtpEntryProps = {
  value: string;
  onChange: (value: string) => void;
  error?: string;
  disabled?: boolean;
  resendAt?: number | null;
  countdownActive?: boolean;
  isResending?: boolean;
  onResend?: () => void;
  helperText?: string;
};

type PhoneOtpResendButtonProps = {
  resendAt?: number | null;
  countdownActive: boolean;
  disabled: boolean;
  isResending: boolean;
  onResend: () => void;
};

const PhoneOtpResendButton = memo(
  ({
    resendAt,
    countdownActive,
    disabled,
    isResending,
    onResend,
  }: PhoneOtpResendButtonProps) => {
    const [, setCountdownTick] = useState(0);

    useEffect(() => {
      if (!countdownActive || !resendAt || resendAt <= Date.now()) {
        return undefined;
      }

      const timer = window.setInterval(() => {
        setCountdownTick((tick) => tick + 1);
        if (resendAt <= Date.now()) window.clearInterval(timer);
      }, 1000);
      return () => window.clearInterval(timer);
    }, [countdownActive, resendAt]);

    const resendSeconds =
      countdownActive && resendAt
        ? Math.max(0, Math.ceil((resendAt - Date.now()) / 1000))
        : 0;

    return (
      <Button
        onClick={onResend}
        disabled={disabled || isResending || resendSeconds > 0}
        sx={{
          alignSelf: "flex-start",
          px: 0,
          minHeight: 44,
          textTransform: "none",
          fontWeight: 500,
        }}
      >
        {isResending
          ? "Sending…"
          : resendSeconds > 0
            ? `Resend in ${resendSeconds}s`
            : "Resend code"}
      </Button>
    );
  },
);

const PhoneOtpEntry = ({
  value,
  onChange,
  error = "",
  disabled = false,
  resendAt,
  countdownActive = true,
  isResending = false,
  onResend,
  helperText,
}: PhoneOtpEntryProps) => {
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const updateDigit = useCallback(
    (index: number, rawValue: string) => {
      const digits = rawValue.replace(/\D/g, "").slice(0, OTP_LENGTH - index);
      if (!digits) {
        const next = value.padEnd(OTP_LENGTH, " ").split("");
        next[index] = " ";
        onChange(next.join(""));
        return;
      }

      const next = value.padEnd(OTP_LENGTH, " ").split("");
      digits.split("").forEach((digit, offset) => {
        next[index + offset] = digit;
      });
      onChange(next.join(""));
      inputRefs.current[
        Math.min(index + digits.length, OTP_LENGTH - 1)
      ]?.focus();
    },
    [onChange, value],
  );

  const handlePaste = useCallback(
    (event: React.ClipboardEvent<HTMLInputElement>) => {
      const pastedCode = event.clipboardData
        .getData("text")
        .replace(/\D/g, "")
        .slice(0, OTP_LENGTH);
      if (!pastedCode) return;
      event.preventDefault();
      onChange(pastedCode.padEnd(OTP_LENGTH, " "));
      inputRefs.current[Math.min(pastedCode.length, OTP_LENGTH) - 1]?.focus();
    },
    [onChange],
  );

  return (
    <Stack spacing={1.25}>
      <Stack direction="row" spacing={1} justifyContent="flex-start">
        {OTP_DIGIT_KEYS.map((key, index) => (
          <Box
            key={key}
            component="input"
            ref={(element: HTMLInputElement | null) => {
              inputRefs.current[index] = element;
            }}
            aria-label={`Verification code digit ${index + 1} of ${OTP_LENGTH}`}
            aria-invalid={Boolean(error)}
            inputMode="numeric"
            autoComplete={index === 0 ? "one-time-code" : "off"}
            maxLength={index === 0 ? OTP_LENGTH : 1}
            value={value[index]?.trim() ?? ""}
            disabled={disabled}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              updateDigit(index, event.target.value)
            }
            onPaste={handlePaste}
            onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
              if (
                event.key === "Backspace" &&
                (!value[index] || /\s/.test(value[index])) &&
                index > 0
              ) {
                inputRefs.current[index - 1]?.focus();
              } else if (event.key === "ArrowLeft" && index > 0) {
                inputRefs.current[index - 1]?.focus();
              } else if (event.key === "ArrowRight" && index < OTP_LENGTH - 1) {
                inputRefs.current[index + 1]?.focus();
              }
            }}
            sx={{
              width: { xs: 42, sm: 48 },
              height: 52,
              border: "1px solid",
              borderColor: error ? "error.main" : "#CBD5E1",
              borderRadius: 1.5,
              textAlign: "center",
              font: "inherit",
              fontSize: "1.25rem",
              fontWeight: 600,
              color: "#0F172A",
              backgroundColor: "#FFFFFF",
              outline: "none",
              "&:focus-visible": {
                borderColor: "primary.main",
                boxShadow: (theme) =>
                  `0 0 0 3px ${theme.palette.primary.light}55`,
              },
            }}
          />
        ))}
      </Stack>
      {error && (
        <Typography color="error" role="alert" variant="body2">
          {error}
        </Typography>
      )}
      {helperText && (
        <Typography variant="caption" color="text.secondary">
          {helperText}
        </Typography>
      )}
      {onResend && (
        <PhoneOtpResendButton
          resendAt={resendAt}
          countdownActive={countdownActive}
          disabled={disabled}
          isResending={isResending}
          onResend={onResend}
        />
      )}
    </Stack>
  );
};

export default memo(PhoneOtpEntry);
